// @ts-nocheck - Amplify Gen 2 type compatibility issues
/**
 * G12r Tariff Calculator (Energa-specific Extended Weekend Tariff)
 *
 * @description Calculates costs for G12r rural tariff (Energa-specific)
 * @version 2.1.0
 * @created 2025-01-26
 * @author eCompanion2 Energy Tariff Calculator
 *
 * Extracted from tariff-calculator.ts for better modularity
 */

import type { G12rPricing, MonthlyResult, ConsumptionPattern } from './types';
import {
  VAT_RATE,
  HANDLING_FEE,
  SUBSCRIPTION_FEE,
  TRANSITION_FEE,
  POWER_FEE,
  OZE_FEE,
  COGENERATION_FEE,
  isPowerFeeFrozen,
} from './constants';
import { roundToTwoDecimals, distributeConsumption } from './helpers';

/**
 * Calculate G12r (Energa-specific Extended Weekend Tariff) cost
 *
 * Formula (Energa-specific time windows):
 * - Peak hours: 07:00-13:00 + 16:00-22:00 weekdays (Ekonomiczna Dolina - 3h midday valley)
 * - Off-peak hours: 13:00-16:00 + 22:00-07:00 weekdays + all day weekends/holidays (cheap rate)
 * - Energy cost = kWh × energyPrice + qualityCharge × totalKwh
 * - Distribution cost = fixedFee + (peakKwh × distPeak) + (offPeakKwh × distOffPeak)
 * - Total (gross) = subtotal × 1.23
 *
 * @param monthlyConsumption - kWh consumed in month
 * @param pricing - G12r pricing data
 * @param consumptionPattern - Hourly pattern (weekday/weekend)
 * @param daysInMonth - Number of days in month
 * @param year - Year (for holiday detection)
 * @param month - Month (0-indexed, 0=Jan)
 * @returns Monthly calculation result with peak/off-peak split
 */
export function calculateG12r(
  monthlyConsumption: number,
  pricing: G12rPricing,
  consumptionPattern: ConsumptionPattern,
  daysInMonth: number,
  year: number,
  month: number
): MonthlyResult {
  // Input validation - HIGH-1 Security: Validate for NaN, Infinity, and negative values
  if (!Number.isFinite(monthlyConsumption) || monthlyConsumption < 0) {
    throw new Error('Monthly consumption must be a finite non-negative number');
  }
  if (!pricing) {
    throw new Error('Pricing data is required');
  }
  if (!consumptionPattern) {
    throw new Error('Consumption pattern is required');
  }

  // Distribute consumption across hours
  const hourlyConsumption = distributeConsumption(
    monthlyConsumption,
    consumptionPattern,
    daysInMonth,
    year,
    month
  );

  // Energa G12r: Peak is 07:00-13:00 + 16:00-22:00 weekdays (Ekonomiczna Dolina), off-peak is 13:00-16:00 + 22:00-07:00 weekdays + all day weekends/holidays
  let peakKwh = 0;
  let offPeakKwh = 0;

  for (const hourData of hourlyConsumption) {
    const isWeekendOrHoliday = hourData.isWeekend || hourData.isHoliday;

    if (isWeekendOrHoliday) {
      // All weekend/holiday hours are off-peak (cheap)
      offPeakKwh += hourData.consumption;
    } else {
      // Weekday: peak is 07:00-13:00 + 16:00-22:00 (Ekonomiczna Dolina: 13:00-16:00 is off-peak)
      if ((hourData.hour >= 7 && hourData.hour < 13) || (hourData.hour >= 16 && hourData.hour < 22)) {
        peakKwh += hourData.consumption;
      } else {
        offPeakKwh += hourData.consumption;
      }
    }
  }

  // Calculate energy production cost (same rate for all zones)
  const energyPriceTotal = pricing.energyPricePerKwh + (pricing.qualityCharge || 0);
  const productionCost = monthlyConsumption * energyPriceTotal;

  // Calculate distribution cost (different rates for peak/off-peak)
  const distributionPeakCost = peakKwh * pricing.distributionPeakPerKwh;
  const distributionOffPeakCost = offPeakKwh * pricing.distributionOffPeakPerKwh;
  const distributionCost = pricing.fixedMonthlyFee + distributionPeakCost + distributionOffPeakCost;

  // Get fees - use defaults if not provided
  const handlingFee = pricing.handlingFee ?? HANDLING_FEE.REGULATED;
  const subscriptionFee = pricing.subscriptionFee ?? SUBSCRIPTION_FEE;
  const transitionFee = pricing.transitionFee ?? TRANSITION_FEE.TIER_3;

  // Power fee is 0 for Jan-June 2025
  const basePowerFee = pricing.powerFee ?? POWER_FEE.TIER_3;
  const powerFee = isPowerFeeFrozen(month, year) ? 0 : basePowerFee;

  // Variable fees (per kWh)
  const ozeFeeAmount = monthlyConsumption * (pricing.ozeFee ?? OZE_FEE);
  const cogenerationFeeAmount = monthlyConsumption * (pricing.cogenerationFee ?? COGENERATION_FEE);

  // Calculate totals
  const subtotal = productionCost + distributionCost +
    handlingFee + subscriptionFee + transitionFee + powerFee +
    ozeFeeAmount + cogenerationFeeAmount;
  const totalCost = subtotal * (1 + VAT_RATE);

  return {
    totalKwh: roundToTwoDecimals(monthlyConsumption),
    peakKwh: roundToTwoDecimals(peakKwh),
    offPeakKwh: roundToTwoDecimals(offPeakKwh),
    productionCost: roundToTwoDecimals(productionCost),
    distributionCost: roundToTwoDecimals(distributionCost),
    handlingFee: roundToTwoDecimals(handlingFee),
    subscriptionFee: roundToTwoDecimals(subscriptionFee),
    transitionFee: roundToTwoDecimals(transitionFee),
    powerFee: roundToTwoDecimals(powerFee),
    ozeFee: roundToTwoDecimals(ozeFeeAmount),
    cogenerationFee: roundToTwoDecimals(cogenerationFeeAmount),
    subtotal: roundToTwoDecimals(subtotal),
    totalCost: roundToTwoDecimals(totalCost)
  };
}
